// Key-Center Panel Key — one clickable key of the key-center keyboard.
// Displays its scale degree colour relative to the key center, and sets the
// key center when clicked. App.tsx maps it over the white keys and again
// over the black keys.

import React, { useState } from 'react';
import { getSpellings, midiToName, getModeFromPcSet } from '../utils';
import { useColors, computeShadowColor, computeBlackKeyColor, computeBlackKeyShadowColor, ColorScheme } from '../theme/colorThemeContext';

type KeyCenterPanelKeyProps = {
  note: number;
  isBlack: boolean;
  rootNote: number;
  setRootNote: (note: number) => void;
  rootPc: number | null;
  keyCenter: number;
  setKeyCenter: (note: number) => void;
  style?: React.CSSProperties;
  isActive: boolean;
  isScale: boolean;
  toggleLock: number | null;
  setToggleLock: (note: number | null) => void;
  mode: number;
  chordQual: number;
  lockedTonicNote: number | null;
  lockedTonicMode: number | null;
  isChartLocked: boolean; // the lock is the chart's key center, not the user's
  isFirstOccurrence: boolean;
  appTheme: string;
  showTonicScale: boolean;
  showRelativeChordScale: boolean;
  showChordScale: boolean;
  tonicScalesNotes: number[][];
  relativeChordScalesNotes: number[][];
  chordScalesNotes: number[][];
  scaleIndex: number;
  // isImpliedScaleRef: boolean;
  // changeKey: number;
};

const KeyCenterPanelKey: React.FC<KeyCenterPanelKeyProps> = ({
  note,
  isBlack,
  rootNote,
  setRootNote,
  rootPc,
  keyCenter,
  setKeyCenter,
  isActive,
  isScale,
  style,
  toggleLock,
  setToggleLock,
  mode,
  chordQual,
  lockedTonicNote,
  lockedTonicMode,
  isChartLocked,
  isFirstOccurrence,
  appTheme,
  showTonicScale,
  showRelativeChordScale,
  showChordScale,
  tonicScalesNotes,
  relativeChordScalesNotes,
  chordScalesNotes,
  scaleIndex,
  // isImpliedScaleRef,
  // changeKey
}) => {
  // console.log("######################### rootNote, note, toggleLock", rootNote, note, toggleLock)
  const pc = pitchClass(note, keyCenter);
  const pcClassNames = ['p-root', 'min-second', 'maj-second', 'min-third', 'maj-third', 'p-fourth', 'tritone', 'p-fifth', 'min-sixth', 'maj-sixth', 'min-seventh', 'maj-seventh'];
  const pcClass = pcClassNames[pc];
  // const isToggledRoot = note % 12 === keyCenter && toggleLock !== null && isFirstOccurrence;
  const isToggledRoot = note % 12 === keyCenter && toggleLock !== null && (isFirstOccurrence === undefined || isFirstOccurrence);
  const isToggleOn = toggleLock !== null;
  // console.log('isToggledRoot and isToggleOn', isToggledRoot, isToggleOn)
  const blackPrefix = !isBlack && !isToggleOn ? '' : 'black-';
  const scalePrefix = isToggleOn && !isToggledRoot ? 'colkey-scale-' : '';
  const keyClassName = `color-key ${isBlack ? 'black' : 'white'} ${isToggledRoot ? 'ck-toggled' : ''} ${isToggledRoot && appTheme === 'light' ? 'ck-toggled-light' : ''} ${(isActive || isToggledRoot) ? blackPrefix + scalePrefix + pcClass : 'inactive-scale-note'}`;

  const { colors, followModeSoftColors, followModeSoftLightColors } = useColors();

  const pcToColorKey: { [key: string]: string } = {
    'p-root': 'R',
    'min-second': 'm2',
    'maj-second': 'M2',
    'min-third': 'm3',
    'maj-third': 'M3',
    'p-fourth': 'P4',
    'tritone': 'd5',
    'p-fifth': 'P5',
    'min-sixth': 'm6',
    'maj-sixth': 'M6',
    'min-seventh': 'm7',
    'maj-seventh': 'M7'
  };

  const computeBlackKeyColor = (rgbColor: string): string => {
    // Parse the RGB color
    const rgbMatch = rgbColor.match(/rgb\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*\)/);
    if (!rgbMatch) return rgbColor; // Return original if not a valid RGB color

    // Extract RGB components
    const r = parseInt(rgbMatch[1], 10);
    const g = parseInt(rgbMatch[2], 10);
    const b = parseInt(rgbMatch[3], 10);

    // Fixed target color values
    const targetR = 0;
    const targetG = 0;
    const targetB = 0;

    // Calculate the average between the input color and target color
    const avgR = Math.floor((r * 5 + targetR * 1) / 6.5);
    const avgG = Math.floor((g * 5 + targetG * 1) / 6.5);
    const avgB = Math.floor((b * 5 + targetB * 1) / 6.5);

    return `rgb(${avgR}, ${avgG}, ${avgB})`;
  }

  const computeShadowColor = (rgbColor: string): string => {
    // Parse the RGB color
    const rgbMatch = rgbColor.match(/rgb\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*\)/);
    if (!rgbMatch) return rgbColor; // Return original if not a valid RGB color

    // Extract RGB components
    const r = parseInt(rgbMatch[1], 10);
    const g = parseInt(rgbMatch[2], 10);
    const b = parseInt(rgbMatch[3], 10);

    // Fixed target color values
    const targetR = 20;
    const targetG = 20;
    const targetB = 40;

    // Calculate the average between the input color and target color
    const avgR = Math.floor((r + targetR * 1) / 1.1); // 1.4
    const avgG = Math.floor((g + targetG * 1) / 1.1); // 1.4
    const avgB = Math.floor((b + targetB * 1) / 1.1); // 1.4

    return `rgb(${avgR}, ${avgG}, ${avgB})`;
  }

  const scaleMode = showTonicScale ? getModeFromPcSet(tonicScalesNotes[0]) : showRelativeChordScale ? getModeFromPcSet(relativeChordScalesNotes[scaleIndex]) : showChordScale ? getModeFromPcSet(chordScalesNotes[scaleIndex]) : mode;

  // console.log('chordQualllllllllllllllllll', chordQual);

  const getBaseColor = (key: string) => {
    const colorKey = pcToColorKey[key];

    // // Override root note color to orange if appTheme is "light"
    // if (key === 'p-root' && appTheme === 'light') {
    //   return 'rgb(255, 0, 0)';
    // }

    // return 'rgb(145, 255, 255)';
    // if (!colorKey || !colors[colorKey as keyof ColorScheme]) {
    if (!colorKey || !followModeSoftColors[colorKey as keyof ColorScheme]) {
      console.warn(`No color found for key: ${key}, colorKey: ${colorKey}, mode: ${mode}, available colors:`, Object.keys(colors));
      return 'rgb(200, 200, 200)'; // fallback color
    }
    // return colors[colorKey as keyof ColorScheme][mode];
    // console.log('LOCKED TONIC MODE IS:', isToggleOn, mode, lockedTonicMode, lockedTonicNote, isToggleOn, )
    return appTheme === 'light'
      // ? followModeSoftLightColors[colorKey as keyof ColorScheme][isToggleOn && lockedTonicNote !== null && lockedTonicMode !== null ? lockedTonicMode : mode] // OLD METHOD tied to lockedTonicMode
      ? followModeSoftLightColors[colorKey as keyof ColorScheme][scaleMode]
      : followModeSoftColors[colorKey as keyof ColorScheme][scaleMode]; // mode for current chord or scale mode color
  };

  const getShadowColor = (key: string) => {
    // return 'rgb(171, 255, 255)';
    return computeShadowColor(getBaseColor(key));
  };

  const getBlackKeyColor = (key: string) => {
    // return 'rgb(0, 189, 189)';
    return computeBlackKeyColor(getBaseColor(key));
  };

  const getBlackKeyShadowColor = (key: string) => {
    // return 'rgb(66, 66, 66)';
    return computeBlackKeyShadowColor(getBaseColor(key));
  };

  const keyStyle: React.CSSProperties = {
    ...style,
    // borderBottomWidth: isActive || isScale || toggleLock !== null ? '0.1dvw' : toggleLock !== null && isActive ? '0dvw' : '0.4dvw',
    // borderTopWidth: !isActive || isScale || toggleLock !== null ? '0.0dvw' : toggleLock !== null && isActive ? '0.1dvw' : '0.4dvw', // fix this to add border for active notes when toggleLock
    // borderBottomStyle: !isActive ? 'solid' : toggleLock !== null && isActive ? 'inset' : 'outset',
    // borderColor: isScale ? 'rgb(90,90,90' : toggleLock !== null && isActive ? 'white' : 'rgb(40,40,40',
    // borderBottomRightRadius: isActive ? '0.3dvw 0.3dvw' : '0.4dvw 0.4dvw',
    // borderBottomLeftRadius: isActive ? '0.3dvw 0.3dvw' : '0.4dvw 0.4dvw',

    // color: isActive ? `var(--color-${pcClass.split('-').pop()}-shadow)` : undefined,
    // borderColor: isScale ? 'rgb(120,120,120' : 'rgb(40,40,40',
    // opacity: !isScale || (isScale && isActive) ? 1 : 1,
    // opacity: isActive ? 1 : 0.8,
    // borderColor: !isScale || (isScale && !isActive) ? 'rgb(220,220,220' : '#000',
    // borderWidth: !isScale || (isScale && isActive) ? null : 0,
    // borderBottomColor: 'rgb(120,120,120)',
    // borderBottomColor: isActive ? 'grey' : 'rgb(120,120,120)',
    // borderBottomRightRadius: '4px 4px',
    // borderBottomLeftRadius: '4px 4px',
    // borderTopWidth: isBlack && !isActive ? 0 : 4,
    // border: '0.5px solid rgb(120,120,120)',
    // border: !isScale || (isScale && !isActive) ? '0.5px solid rgb(120,120,120)' : '0.8px solid white',
    // color: !isScale || (isScale && isActive) ? '#fff' : '#000',
    // backfaceVisibility: 'hidden'
    cursor: 'pointer',
    background: (isActive || isToggledRoot) ?
      `linear-gradient(${isBlack ? getBlackKeyShadowColor(pcClass) : getShadowColor(pcClass)}, 
       ${isBlack ? getBlackKeyColor(pcClass) : getBaseColor(pcClass)} 
       ${isBlack ? 'var(--black-shadow-location)' : 'var(--shadow-location)'})` :
      undefined
  };

  // console.log('isImpliedScaleisImpliedScaleisImpliedScaleisImpliedScale', isImpliedScaleRef)

  // const handleClick = () => {
  //   if (toggleLock === null || toggleLock === note) {
  //     setRootNote(note);
  //     setToggleLock(toggleLock === note ? null : note);
  //   }
  // };

  const handleClick = () => {
    // console.log("handleClick", note && note, toggleLock && toggleLock)
    let theNote = note <= 11 ? note : note % 12;
    // A chart lock is not the user's to untoggle: clicking its tonic makes it the user's lock
    if (toggleLock === theNote && !isChartLocked) { // If the current note is already toggled, untoggle it
      setToggleLock(null);
    } else { // Toggle on the new note and set it as the rootNote, overriding scale root
      // toggleLock ? setRootNote(note) : setKeyCenter(note);
      // setToggleLock moves rootNote itself, and only under the Key-Center
      // Anchor setting; setting it here would swing the Chord Compass to the
      // tonic under Chord Root Anchor too.
      setKeyCenter(theNote)
      setToggleLock(theNote);
    }
  };

  const [isHovered, setIsHovered] = useState(false);

  return (
    <div
      data-actionable="true"
      //       title="Lock the key to simulate prior context (key center perception.)&#13;&#13;
      // • To lock a key center, click a piano key and select 'Major' or 'Minor.' The key is locked to the 'Root' and all scale degree colors are shown. The Sentiments Palette changes to 'Key Center Emotions.'&#13;&#13;
      // • To unlock, click the piano key again."
      // title="Click a key to lock/unlock the key center"
      className={keyClassName}
      style={keyStyle}
      onClick={handleClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* <div className="name-display">{midiToName(note, false, 0, 0)}</div> */}

      {/* Add note name display for white keys */}
      {/* {[0, 2, 4, 5, 7, 9, 11].includes(note % 12) && ( */}
      {(isActive || isHovered || (note % 12 === rootNote && isActive) || (isToggledRoot && note % 12 === keyCenter)) && (
        <div className="color-key-note-name" style={{
          color: isActive || isToggledRoot ? 'rgb(40, 40, 40)' : 'rgb(175, 175, 175)',
          fontWeight: note % 12 === rootNote ? 900 : isHovered ? 900 : 100,
          fontSize: note % 12 === rootNote ? '0.8dvw' : isHovered ? '0.8dvw' : '0.7dvw',
          marginBottom: isHovered && !isActive ? '0.4dvw' : '0.4dvw',
        }}>
          {
            midiToName(note, false, lockedTonicNote !== null
              ? lockedTonicNote : rootPc
                ? rootPc : rootNote,
              lockedTonicNote !== null && lockedTonicMode !== null
                ? getModeFromPcSet(tonicScalesNotes[0]) : chordQual) // was scaleMode
            // ['C', 'D', 'E', 'F', 'G', 'A', 'B'][
            //   [0, 2, 4, 5, 7, 9, 11].indexOf(note % 12)
            // ]
          }
        </div>
      )}

    </div>
  );
};

const pitchClass = (note: number, keyCenter: number) => {
  return (note - keyCenter + 12) % 12;
};

export default React.memo(KeyCenterPanelKey);
